import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ImagePlus, Info, Loader2, Send, Trash2 } from 'lucide-react';
import { NotificationSendResult, NotificationType } from '@/services/school.api';
import { BroadcastResult, OrgNotificationsApi } from '@/services/org-notifications.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Pagination } from '@/components/ui/Pagination';
import { cn } from '@/lib/cn';

const AUDIENCE_LABEL: Record<NotificationType, string> = {
  all: 'All users',
  specific: 'Specific users',
  roles: 'Roles',
  overdue_fees: 'Overdue Fees',
  specific_class: 'Specific Class',
};

const TYPE_TONE: Record<NotificationType, 'slate' | 'blue' | 'indigo' | 'amber'> = {
  all: 'blue',
  specific: 'indigo',
  roles: 'slate',
  overdue_fees: 'amber',
  specific_class: 'indigo',
};

const ROLE_OPTIONS = ['owner', 'admin', 'manager', 'teacher', 'staff', 'cashier', 'student', 'parent'];

const composeSchema = z.object({
  title: z.string().min(1, 'Required').max(255),
  message: z.string().min(1, 'Required').max(2000),
  type: z.enum(['all', 'specific', 'roles', 'overdue_fees', 'specific_class']),
});
type ComposeForm = z.infer<typeof composeSchema>;

const TABS = ['school', 'schools'] as const;
type Tab = (typeof TABS)[number];

/**
 * Org tier — Notifications across every school in scope. Composing still
 * needs ONE (or several, via "Multiple schools") target school(s) — our
 * schema-per-tenant architecture has to write into a specific schema — but
 * VIEWING history does not: it's flat by default, no "pick a school
 * first" step, matching real legacy's own list view (confirmed directly
 * against the real NotificationController/view: no school picker, no
 * per-row school column at all — unscoped for a true Super Admin there).
 * The school filter on History is optional, not a gate. See the backend's
 * sliders.service.ts "Org tier" doc comment for the fan-out mechanics
 * behind this.
 *
 * Compose has two tabs: "One school" (full compose, all 5 audience types)
 * and "Multiple schools" — matches real legacy's "For specific schools"
 * audience type (Super-Admin-only there), adapted here as an Org Admin
 * broadcasting the same "all users" message across several of their own
 * schools in one submit. No Settings tab here — automatic-notification
 * category on/off is a school-tier-only concern.
 */
export function OrgNotificationsPage() {
  const [tab, setTab] = useState<Tab>('school');
  const [schoolId, setSchoolId] = useState('');
  const { data: schools = [] } = useQuery({ queryKey: ['org-notifications-schools'], queryFn: OrgNotificationsApi.schools });

  return (
    <>
      <PageHeader title="Notifications" description="Compose and send push notifications for schools in scope, and review dispatch history." />

      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">Compose</h2>
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {(['school', 'schools'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium',
              tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {t === 'school' ? 'One school' : 'Multiple schools'}
          </button>
        ))}
      </div>

      {tab === 'school' ? (
        <>
          <div className="mb-4">
            <Select className="w-64" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
              <option value="">— Select a school —</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>

          {!schoolId ? (
            <div className="card mb-6 p-8 text-center text-sm text-slate-400">Select a school to compose.</div>
          ) : (
            <ComposeCard schoolId={schoolId} />
          )}
        </>
      ) : (
        <BroadcastCard schools={schools} />
      )}

      <HistoryTable schools={schools} />
    </>
  );
}

function BroadcastCard({ schools }: { schools: { id: string; name: string }[] }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedSchools, setSelectedSchools] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [result, setResult] = useState<BroadcastResult | null>(null);

  const toggleSchool = (id: string) =>
    setSelectedSchools((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const send = useMutation({
    mutationFn: () =>
      OrgNotificationsApi.createBroadcast({
        schoolIds: [...selectedSchools],
        title,
        message,
        imageUrl: imageUrl || undefined,
      }),
    onSuccess: (res) => {
      setResult(res);
      setTitle('');
      setMessage('');
      setImageUrl('');
      setSelectedSchools(new Set());
      qc.invalidateQueries({ queryKey: ['org-notifications'] });
    },
  });

  async function handleFilePick(file: File) {
    const anySchool = [...selectedSchools][0];
    if (!anySchool) {
      setUploadError('Pick at least one school before uploading an image');
      return;
    }
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await OrgNotificationsApi.upload(anySchool, file);
      setImageUrl(res.url);
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  const valid = !!title && !!message && selectedSchools.size > 0;

  return (
    <div className="card mb-6 p-5">
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-500">Broadcast to multiple schools</h2>
      <p className="mb-4 text-sm text-slate-500">Sends the same "All users" notification to every school picked below, in one submit.</p>

      <div className="space-y-4">
        <Field label="Title" required>
          <Input placeholder="e.g. Platform maintenance notice" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Message" required>
          <Textarea rows={3} placeholder="Notification body" value={message} onChange={(e) => setMessage(e.target.value)} />
        </Field>
        <Field label="Image (optional)">
          {imageUrl ? (
            <div className="relative w-48">
              <img src={imageUrl} alt="" className="aspect-[16/9] w-full rounded-md border border-slate-200 object-cover" />
              <label className="absolute inset-x-0 bottom-0 flex cursor-pointer items-center justify-center gap-1.5 rounded-b-md bg-black/50 py-1 text-xs text-white hover:bg-black/60">
                <ImagePlus className="h-3 w-3" /> Replace
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleFilePick(e.target.files[0])} />
              </label>
            </div>
          ) : (
            <label className="flex w-48 cursor-pointer items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-slate-300 py-3 text-sm text-slate-500 hover:border-brand-400 hover:text-brand-600">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {uploading ? 'Uploading…' : 'Upload'}
              <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => e.target.files?.[0] && handleFilePick(e.target.files[0])} />
            </label>
          )}
          {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
        </Field>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-700">Schools</span>
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-3">
            {schools.map((s) => (
              <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                <input type="checkbox" checked={selectedSchools.has(s.id)} onChange={() => toggleSchool(s.id)} />
                <span className="text-slate-700">{s.name}</span>
              </label>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-slate-500">{selectedSchools.size} selected</p>
        </div>

        {send.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(send.error)}</div>}

        <div className="flex justify-end">
          <button type="button" className="btn-primary" disabled={!valid || send.isPending} onClick={() => send.mutate()}>
            <Send className="mr-1.5 h-4 w-4" />
            {send.isPending ? 'Sending…' : `Send to ${selectedSchools.size || ''} school${selectedSchools.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      {result && (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="text-slate-700">
            Sent across <span className="font-medium">{result.schools.length}</span> school{result.schools.length === 1 ? '' : 's'} —{' '}
            <span className="font-medium">{result.recipientCount}</span> total recipients, {result.reachableCount} with a registered device.
          </div>
          <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
            {result.schools.map((s) => (
              <li key={s.schoolId}>
                {s.schoolName}: {s.recipientCount} recipients
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ComposeCard({ schoolId }: { schoolId: string }) {
  const qc = useQueryClient();
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [result, setResult] = useState<NotificationSendResult | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ComposeForm>({
    resolver: zodResolver(composeSchema),
    defaultValues: { type: 'all' },
  });
  const type = watch('type');

  const { data: users = [] } = useQuery({
    queryKey: ['org-notification-users', schoolId],
    queryFn: () => OrgNotificationsApi.lookupUsers(schoolId),
    enabled: type === 'specific',
  });
  const filteredUsers = useMemo(
    () => users.filter((u) => u.name.toLowerCase().includes(userSearch.toLowerCase())),
    [users, userSearch],
  );

  const { data: sections = [] } = useQuery({
    queryKey: ['org-notification-sections', schoolId],
    queryFn: () => OrgNotificationsApi.lookupSections(schoolId),
    enabled: type === 'specific_class',
  });

  const create = useMutation({
    mutationFn: (f: ComposeForm) =>
      OrgNotificationsApi.create({
        schoolId,
        title: f.title,
        message: f.message,
        type: f.type,
        userIds: f.type === 'specific' ? [...selectedUserIds] : undefined,
        roles: f.type === 'roles' ? [...selectedRoles] : undefined,
        sectionIds: f.type === 'specific_class' ? [...selectedSections] : undefined,
      }),
    onSuccess: (res) => {
      setResult(res);
      reset({ title: '', message: '', type: 'all' });
      setSelectedUserIds(new Set());
      setSelectedRoles(new Set());
      setSelectedSections(new Set());
      qc.invalidateQueries({ queryKey: ['org-notifications'] });
    },
  });

  const toggleUser = (id: string) =>
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleRole = (role: string) =>
    setSelectedRoles((prev) => {
      const next = new Set(prev);
      next.has(role) ? next.delete(role) : next.add(role);
      return next;
    });
  const toggleSection = (id: string) =>
    setSelectedSections((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const canSubmit =
    (type !== 'specific' || selectedUserIds.size > 0) &&
    (type !== 'roles' || selectedRoles.size > 0) &&
    (type !== 'specific_class' || selectedSections.size > 0);

  return (
    <div className="card mb-6 p-5">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">Compose</h2>
      <form onSubmit={handleSubmit((f) => create.mutate(f))} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
          <Input placeholder="e.g. Fee reminder" {...register('title')} />
          {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Message</label>
          <Textarea rows={3} placeholder="Notification body" {...register('message')} />
          {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message.message}</p>}
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Audience</label>
          <Select {...register('type')}>
            {(Object.keys(AUDIENCE_LABEL) as NotificationType[]).map((t) => (
              <option key={t} value={t}>
                {AUDIENCE_LABEL[t]}
              </option>
            ))}
          </Select>
        </div>

        {type === 'specific' && (
          <div className="rounded-md border border-slate-200 p-3">
            <Input
              className="mb-2"
              placeholder="Search users…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {filteredUsers.map((u) => (
                <label key={u.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={selectedUserIds.has(u.id)} onChange={() => toggleUser(u.id)} />
                  <span className="text-slate-700">{u.name}</span>
                  <span className="text-xs capitalize text-slate-400">{u.role}</span>
                </label>
              ))}
              {filteredUsers.length === 0 && <p className="px-2 py-1 text-sm text-slate-400">No users found.</p>}
            </div>
            <p className="mt-2 text-xs text-slate-500">{selectedUserIds.size} selected</p>
          </div>
        )}

        {type === 'roles' && (
          <div className="rounded-md border border-slate-200 p-3">
            <div className="flex flex-wrap gap-3">
              {ROLE_OPTIONS.map((role) => (
                <label key={role} className="flex cursor-pointer items-center gap-1.5 text-sm capitalize">
                  <input type="checkbox" checked={selectedRoles.has(role)} onChange={() => toggleRole(role)} />
                  {role}
                </label>
              ))}
            </div>
          </div>
        )}

        {type === 'specific_class' && (
          <div className="rounded-md border border-slate-200 p-3">
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {sections.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={selectedSections.has(s.id)} onChange={() => toggleSection(s.id)} />
                  <span className="text-slate-700">{s.name}</span>
                </label>
              ))}
              {sections.length === 0 && <p className="px-2 py-1 text-sm text-slate-400">No classes found.</p>}
            </div>
            <p className="mt-2 text-xs text-slate-500">{selectedSections.size} selected</p>
          </div>
        )}

        {type === 'overdue_fees' && (
          <div className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-sm text-slate-600">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            Notifies every student with an overdue, unpaid fee demand (due date already passed), plus their linked guardian(s).
          </div>
        )}

        {create.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>}

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={create.isPending || !canSubmit}>
            <Send className="mr-1.5 h-4 w-4" />
            {create.isPending ? 'Sending…' : 'Send notification'}
          </button>
        </div>
      </form>

      {result && (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="text-slate-700">
            Targeted <span className="font-medium">{result.recipientCount}</span> recipient
            {result.recipientCount === 1 ? '' : 's'} — <span className="font-medium">{result.reachableCount}</span> with a
            registered device.
          </div>
          {result.send.attempted ? (
            <div className="mt-1 text-green-700">
              Delivered to {result.send.successCount ?? 0} device{result.send.successCount === 1 ? '' : 's'}
              {(result.send.failureCount ?? 0) > 0 ? `, ${result.send.failureCount} failed` : ''}.
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-1.5 text-slate-500">
              <Info className="h-3.5 w-3.5" />
              Push delivery pending Firebase setup — the notification is recorded and recipients are resolved, but no device push was sent yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryTable({ schools }: { schools: { id: string; name: string }[] }) {
  const qc = useQueryClient();
  const [filterSchoolId, setFilterSchoolId] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const { data, isLoading } = useQuery({
    queryKey: ['org-notifications', filterSchoolId, page, limit],
    queryFn: () => OrgNotificationsApi.list(filterSchoolId || undefined, { page, limit }),
    placeholderData: keepPreviousData,
  });
  const items = data?.items ?? [];

  const remove = useMutation({
    mutationFn: (n: { id: string; schoolId?: string }) => OrgNotificationsApi.remove(n.id, n.schoolId ?? filterSchoolId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-notifications'] }),
  });

  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">History</h2>
        <Select
          className="w-56"
          value={filterSchoolId}
          onChange={(e) => {
            setFilterSchoolId(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>
      {isLoading ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No notifications sent yet.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <Th>Title</Th>
                {!filterSchoolId && <Th>School</Th>}
                <Th>Audience</Th>
                <Th>Recipients</Th>
                <Th>Sent by</Th>
                <Th>Sent at</Th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((n) => (
                <tr key={n.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{n.title}</div>
                    <div className="max-w-md truncate text-xs text-slate-500">{n.message}</div>
                  </td>
                  {!filterSchoolId && <td className="px-4 py-3 text-sm text-slate-600">{n.schoolName ?? '—'}</td>}
                  <td className="px-4 py-3">
                    <Badge tone={TYPE_TONE[n.type]}>{AUDIENCE_LABEL[n.type]}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{n.recipientCount}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{n.sentByName ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{new Date(n.sentAt).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    <button className="text-slate-400 hover:text-red-600" onClick={() => remove.mutate(n)} title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <Pagination
          page={page}
          totalPages={data?.totalPages ?? 1}
          total={data?.total ?? 0}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className={cn('px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500')}>{children}</th>;
}

function errMsg(e: unknown): string | undefined {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
